import React, { useState, useRef, useEffect } from 'react';
import { X, MessageCircle, Maximize2, Minimize2, MoreVertical, Trash2 } from 'lucide-react';
import { ABLE_COLORS } from '../../constants';

/**
 * ChatHeader - Top bar of chat panel
 * Shows Mabel branding + menu + fullscreen/close controls
 */
export default function ChatHeader({ 
  isDesktop, 
  isFullScreen, 
  onToggleFullScreen, 
  onClose,
  onClearChat
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleClearClick = () => {
    setMenuOpen(false);
    onClearChat?.();
  };

  const iconButtonStyle = {
    background: 'transparent',
    border: 'none',
    color: ABLE_COLORS.textMuted,
    cursor: 'pointer',
    padding: '6px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s'
  };

  return (
    <div style={{
      padding: '12px 16px',
      borderBottom: `1px solid ${ABLE_COLORS.border}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: 'white',
      flexShrink: 0
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '10px',
          background: `linear-gradient(135deg, ${ABLE_COLORS.primary} 0%, ${ABLE_COLORS.secondary} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <MessageCircle size={18} color="white" />
        </div>
        <h2 style={{ 
          margin: 0, 
          fontSize: '16px', 
          fontWeight: '600',
          color: ABLE_COLORS.textPrimary 
        }}>
          Mabel
        </h2>
      </div>
      
      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
        {/* Three-dot menu - positioned away from close button */}
        <div style={{ position: 'relative' }} ref={menuRef}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            title="More options"
            style={iconButtonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = ABLE_COLORS.bgSubtle;
              e.currentTarget.style.color = ABLE_COLORS.textSecondary;
            }}
            onMouseLeave={(e) => {
              if (!menuOpen) {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = ABLE_COLORS.textMuted;
              }
            }}
          >
            <MoreVertical size={18} />
          </button>
          
          {/* Dropdown menu */}
          {menuOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '4px',
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
              border: `1px solid ${ABLE_COLORS.border}`,
              minWidth: '160px',
              zIndex: 100,
              overflow: 'hidden'
            }}>
              <button
                onClick={handleClearClick}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  width: '100%',
                  padding: '10px 14px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: ABLE_COLORS.textPrimary,
                  textAlign: 'left',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = ABLE_COLORS.bgSubtle}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <Trash2 size={16} color={ABLE_COLORS.textMuted} />
                Clear conversation
              </button>
            </div>
          )}
        </div>

        {/* Spacer for visual separation */}
        <div style={{ 
          width: '1px', 
          height: '20px', 
          background: ABLE_COLORS.border,
          margin: '0 4px'
        }} />

        {isDesktop && (
          <button
            onClick={onToggleFullScreen}
            title={isFullScreen ? 'Exit full screen (Esc)' : 'Full screen'}
            style={iconButtonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = ABLE_COLORS.bgSubtle;
              e.currentTarget.style.color = ABLE_COLORS.textSecondary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = ABLE_COLORS.textMuted;
            }}
          >
            {isFullScreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
        )}
        <button
          onClick={onClose}
          style={iconButtonStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = ABLE_COLORS.bgSubtle;
            e.currentTarget.style.color = ABLE_COLORS.textSecondary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = ABLE_COLORS.textMuted;
          }}
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
